// API 엔드포인트 유틸리티 함수들

// 기본 API 경로
const API_BASE = '/admin';

// 동적 경로 생성 헬퍼 함수들
export const createApiPath = {
  // 인증 관련
  auth: {
    login: () => `${API_BASE}/auth/login`,
    refresh: () => `${API_BASE}/auth/refresh`,
    logout: () => `${API_BASE}/auth/logout`,
    me: () => `${API_BASE}/auth/me`,
    password: () => `${API_BASE}/auth/password`,
  },

  /**
   * 관리자 계정(admin_user) — feat/admin-email-login.
   * full-access role 전용.
   */
  adminUsers: {
    list: () => `${API_BASE}/admin-users`,
    emailDuplication: () => `${API_BASE}/admin-users/email-duplication`,
    detail: (adminUserId: number | string) =>
      `${API_BASE}/admin-users/${adminUserId}`,
    unlock: (adminUserId: number | string) =>
      `${API_BASE}/admin-users/${adminUserId}/unlock`,
    temporaryPassword: (adminUserId: number | string) =>
      `${API_BASE}/admin-users/${adminUserId}/temporary-password`,
    menuPermissions: (adminUserId: number | string) =>
      `${API_BASE}/admin-users/${adminUserId}/menu-permissions`,
    menuPermissionsMetadata: () =>
      `${API_BASE}/admin-users/menu-permissions/metadata`,
    auditLogs: (adminUserId: number | string) =>
      `${API_BASE}/admin-users/${adminUserId}/audit-logs`,
  },

  /** 감사 로그 전역 조회 */
  auditLogs: {
    list: () => `${API_BASE}/audit-logs`,
  },

  /**
   * 메뉴 설정 관리 — ROLE_MASTER 전용.
   * GET/POST /admin/menus, PATCH/DELETE /admin/menus/{id}, PUT /admin/menus/tree
   */
  menus: {
    list: () => `${API_BASE}/menus`,
    detail: (menuId: number | string) => `${API_BASE}/menus/${menuId}`,
    tree: () => `${API_BASE}/menus/tree`,
  },

  /** 접속 로그 */
  userAccessLogs: {
    list: () => `${API_BASE}/v1/user-access-logs`,
  },

  // 플랫폼 사용자 관련
  users: {
    list: () => `${API_BASE}/platform-users`,
    bulk: () => `${API_BASE}/platform-users/bulk`,
    detailById: (userId: number) => `${API_BASE}/platform-users/${userId}`,
    loginMethods: (userId: number) =>
      `${API_BASE}/platform-users/${userId}/login-methods`,
    withdraw: (userId: number) =>
      `${API_BASE}/platform-users/${userId}/withdraw`,
    resetBusinessLicense: (userId: number) =>
      `${API_BASE}/platform-users/${userId}/business-license`,
    resetPromoLicense: (userId: number) =>
      `${API_BASE}/platform-users/${userId}/promo-license`,
    trainingPresignedUrl: () =>
      `${API_BASE}/platform-users/training-verified/presigned-url`,
    promoPresignedUrl: () =>
      `${API_BASE}/platform-users/promo-license/presigned-url`,
    businessLicensePresignedUrl: () =>
      `${API_BASE}/platform-users/business-license/presigned-url`,
    bankbookPresignedUrl: () =>
      `${API_BASE}/platform-users/bankbook-copy/presigned-url`,
    maxMatchLiteCurrentMonth: (userId: number) =>
      `${API_BASE}/max-match/users/${userId}/lite/current-month`,
    maxMatchLiteUsageReset: (userId: number) =>
      `${API_BASE}/max-match/users/${userId}/lite/usage-resets`,
  },

  // 제약사 관련
  pharma: {
    list: () => `${API_BASE}/pharma-company`,
    /** 제약사 목록 엑셀 — 서버가 S3에 올리고 서명 URL을 돌려준다 */
    export: () => `${API_BASE}/pharma-company/export`,
    detail: (id: number) => `${API_BASE}/pharma-company/${id}`,
    create: () => `${API_BASE}/pharma-company`,
    update: (id: number) => `${API_BASE}/pharma-company/${id}`,
  },

  // 병원 관련
  hospitals: {
    list: () => `${API_BASE}/hospitals`,
    /**
     * 등록된 병의원 주소에서 뽑은 시/도·시/군/구 선택지.
     * 행정구역 기준정보가 아니라 clinic_address 기반이라 병의원이 없는 지역은 없다.
     */
    regions: () => `${API_BASE}/hospitals/regions`,
    /** 병의원 목록 엑셀 — 서버가 S3에 올리고 서명 URL을 돌려준다 */
    export: () => `${API_BASE}/hospitals/export`,
    detail: (id: number) => `${API_BASE}/hospitals/${id}`,
    update: (id: number) => `${API_BASE}/hospitals/${id}`,
    businessLicensePresignedUrl: () =>
      `${API_BASE}/hospitals/business-license/presigned-url`,
    businessLicenseComplete: () =>
      `${API_BASE}/hospitals/business-license/complete`,
  },

  // 약국 관련
  pharmacies: {
    list: () => `${API_BASE}/pharmacies`,
    /** 문전약국 목록 엑셀 — 서버가 S3에 올리고 서명 URL을 돌려준다 */
    export: () => `${API_BASE}/pharmacies/export`,
    detail: (id: number) => `${API_BASE}/pharmacies/${id}`,
    update: (id: number) => `${API_BASE}/pharmacies/${id}`,
  },

  // 병의원·약국 공공 데이터 수집 배치 (의약품 수집 배치와 별개)
  facilityCollection: {
    run: () => `${API_BASE}/batch/facility-collection`,
  },

  // 약품 관련
  drugs: {
    list: () => `${API_BASE}/drugs`,
    /** 의약품 목록 엑셀 — 서버가 S3에 올리고 서명 URL을 돌려준다 */
    export: () => `${API_BASE}/drugs/export`,
    search: () => `${API_BASE}/drugs/search`,
    searchByName: () => `${API_BASE}/drugs/search/drug-name`,
    searchByNames: () => `${API_BASE}/drugs/search/drug-names`,
    searchByCodes: () => `${API_BASE}/drugs/search/drug-codes`,
    detail: () => `${API_BASE}/drugs/detail`,
    /** 공개(released)된 기준 연월 중 가장 최근 월 — 미공개 월은 목록 404 */
    latestReleasedRefYm: () => `${API_BASE}/drugs/latest-released-ref-ym`,
    update: (refYm: string, drugCode: string) =>
      `${API_BASE}/drugs/${encodeURIComponent(refYm)}/${encodeURIComponent(drugCode)}`,
  },

  // 수수료 관련
  commissions: {
    list: () => `${API_BASE}/commissions`,
    /** 정산 수수료 목록 엑셀 — 서버가 S3에 올리고 서명 URL을 돌려준다 */
    export: () => `${API_BASE}/commissions/export`,
    detail: () => `${API_BASE}/commissions/detail`,
    batch: () => `${API_BASE}/commissions/batch`,
  },

  // 영업 제휴 관련
  business: {
    list: () => `${API_BASE}/business-relationships`,
    detail: (id: number) => `${API_BASE}/business-relationships/${id}`,
    update: (id: number) => `${API_BASE}/business-relationships/${id}`,
    companies: (relationPharmaCompanyId: number) =>
      `${API_BASE}/business-relationships/${relationPharmaCompanyId}/companies`,
    /** 선택 행 거래 상태 일괄 변경 — 부분 성공 응답 */
    bulkTransactionStatus: () =>
      `${API_BASE}/business-relationships/transaction-status`,
    exportAll: () => `${API_BASE}/business-relationships/export-all`,
    excelUpload: () => `${API_BASE}/business-relationships/excel-upload`,
  },

  // 업체 관리
  partnerCompanies: {
    list: () => `${API_BASE}/partner-companies`,
    detail: (partnerCompanyId: number | string) =>
      `${API_BASE}/partner-companies/${partnerCompanyId}`,
    create: () => `${API_BASE}/partner-companies`,
    update: (partnerCompanyId: number | string) =>
      `${API_BASE}/partner-companies/${partnerCompanyId}`,
    options: () => `${API_BASE}/partner-companies/options`,
    contracts: (partnerCompanyId: number | string) =>
      `${API_BASE}/partner-companies/${partnerCompanyId}/contracts`,
    contractDetail: (
      partnerCompanyId: number | string,
      contractId: number | string,
    ) =>
      `${API_BASE}/partner-companies/${partnerCompanyId}/contracts/${contractId}`,
    histories: (partnerCompanyId: number | string) =>
      `${API_BASE}/partner-companies/${partnerCompanyId}/histories`,
    uploadUrl: () => `${API_BASE}/partner-companies/documents/upload-url`,
    documents: (partnerCompanyId: number | string) =>
      `${API_BASE}/partner-companies/${partnerCompanyId}/documents`,
    assignments: (parentCompanyId: number | string) =>
      `${API_BASE}/partner-companies/${parentCompanyId}/assignments`,
    assignmentDetail: (
      partnerCompanyId: number | string,
      assignmentId: number | string,
    ) =>
      `${API_BASE}/partner-companies/${partnerCompanyId}/assignments/${assignmentId}`,
    accounts: (partnerCompanyId: number | string) =>
      `${API_BASE}/partner-companies/${partnerCompanyId}/accounts`,
    accountDetail: (
      partnerCompanyId: number | string,
      accountId: number | string,
    ) =>
      `${API_BASE}/partner-companies/${partnerCompanyId}/accounts/${accountId}`,
  },

  // 처방전 관련
  /**
   * 처방 마감일(월별) — 처방등록년월이 넘어가는 기준일.
   * 처방 검수 권한에 묶이지 않도록 `/prescriptions/**` 밖에 있다.
   */
  prescriptionRegistrationDeadlines: {
    list: () => `${API_BASE}/prescription-registration-deadlines`,
    detail: (yearMonth: string) =>
      `${API_BASE}/prescription-registration-deadlines/${yearMonth}`,
  },

  prescriptions: {
    list: () => `${API_BASE}/prescriptions`,
    detail: (code: string) => `${API_BASE}/prescriptions/${code}`,
    /** 처방 이미지·내역 삭제 — DELETE .../prescriptions/{code} (204) */
    delete: (code: string) =>
      `${API_BASE}/prescriptions/${encodeURIComponent(code)}`,
    drugs: (code: string) => `${API_BASE}/prescriptions/${code}/drugs`,
    /** 이전 처방 의약품 — 같은 사용자·병의원의 확정 처방, 처방등록년월 기준 최근 3개월 */
    previousDrugs: (code: string) =>
      `${API_BASE}/prescriptions/${encodeURIComponent(code)}/previous-drugs`,
    registered: () => `${API_BASE}/prescriptions/registered`,
    finalized: () => `${API_BASE}/prescriptions/finalized`,
    finalizedDetail: (id: number) =>
      `${API_BASE}/prescriptions/finalized/${id}`,
    finalizedPharmaCompanies: () =>
      `${API_BASE}/prescriptions/finalized/pharma-companies`,
    /** 확정 처방 내역 엑셀 — 서버가 S3에 올리고 서명 URL을 돌려준다 */
    finalizedExportAllExcel: () =>
      `${API_BASE}/prescriptions/finalized/export-all/excel`,
    /** 확정 처방 이미지 ZIP — 서버가 S3에 올리고 서명 URL을 돌려준다 */
    finalizedExportImages: () =>
      `${API_BASE}/prescriptions/finalized/export-images`,
    drugsCreate: () => `${API_BASE}/prescriptions/drugs`,
    drugsDelete: () => `${API_BASE}/prescriptions/drugs`,
    drugCreate: () => `${API_BASE}/prescriptions/drug`,
    drugUpdate: (id: number) => `${API_BASE}/prescriptions/drug/${id}`,
    drugDelete: (id: number) => `${API_BASE}/prescriptions/drug/${id}`,
    imageUpdate: (code: string) => `${API_BASE}/prescriptions/${code}/image`,
    finalize: () => `${API_BASE}/prescriptions/finalize`,
    finalizeBatch: () => `${API_BASE}/prescriptions/finalize-batch`,
    /** 정산년월 일괄 확정 — 레거시 finalize/settlement-month */
    finalizeSettlementMonth: () =>
      `${API_BASE}/prescriptions/finalize/settlement-month`,
    ocrRetry: () => `${API_BASE}/prescriptions/ocr/retry`,
    presignedUrls: () => `${API_BASE}/prescriptions/presigned-urls`,
    uploadComplete: () => `${API_BASE}/prescriptions/upload-complete`,
    cancelFinalized: (code: string) =>
      `${API_BASE}/prescriptions/${encodeURIComponent(code)}/finalized`,
    finalizedDuplicates: (code: string) =>
      `${API_BASE}/prescriptions/${encodeURIComponent(code)}/finalized/duplicates`,
    /** OCR raw (하이라이터) — 레거시 GET .../ocr-raw */
    ocrRaw: (code: string) =>
      `${API_BASE}/prescriptions/${encodeURIComponent(code)}/ocr-raw`,
    /** 처방 확정 제외 — PATCH 지정(200) / DELETE 해제(204, 멱등) */
    exclusion: (code: string) =>
      `${API_BASE}/prescriptions/${encodeURIComponent(code)}/exclusion`,
  },

  // 정산 관련
  settlements: {
    list: () => `${API_BASE}/settlements`,
    status: () => `${API_BASE}/settlements/status`,
    users: () => `${API_BASE}/settlements/users`,
    /** DELETE — 처방·정산·사용자 키로 settlement 행 일괄 삭제 */
    deleteUserSettlement: () => `${API_BASE}/settlements/users`,
    pharmas: () => `${API_BASE}/settlements/pharmas`,
    clinics: () => `${API_BASE}/settlements/clinics`,
    userDetails: () => `${API_BASE}/settlements/user/details`,
    pharmaCompanies: () => `${API_BASE}/settlements/pharma-companies`,
    months: () => `${API_BASE}/settlements/months`,
    export: () => `${API_BASE}/settlements/export`,
    detail: () => `${API_BASE}/settlements/detail`,
    detailDrugs: () => `${API_BASE}/settlements/detail/drugs`,
    publish: () => `${API_BASE}/settlements/publish`,
    pharmaUpload: () => `${API_BASE}/settlements/pharma/upload`,
    mergePharma: () => `${API_BASE}/settlements/merge-pharma`,
    generate: () => `${API_BASE}/settlements/generate`,
  },

  // OCR 관련
  ocr: {
    promoLicense: () => `${API_BASE}/ocr/promo-license`,
    prescription: () => `${API_BASE}/ocr/prescription`,
    prescriptionFile: () => `${API_BASE}/ocr/prescription/file`,
    businessLicense: () => `${API_BASE}/ocr/business-license`,
  },

  // 계약 별첨·위탁 계약
  contracts: {
    appendix: (refYm: string) =>
      `${API_BASE}/contracts/appendices/${encodeURIComponent(refYm)}`,
    appendixGenerate: (refYm: string) =>
      `${API_BASE}/contracts/appendices/${encodeURIComponent(refYm)}/generate`,
    appendixDownloadUrl: (refYm: string) =>
      `${API_BASE}/contracts/appendices/${encodeURIComponent(refYm)}/download-url`,
    pending: () => `${API_BASE}/contracts/pending`,
    detail: (contractId: string) =>
      `${API_BASE}/contracts/${encodeURIComponent(contractId)}`,
    signedHtml: (contractId: string) =>
      `${API_BASE}/contracts/${encodeURIComponent(contractId)}/signed-html`,
    finalPdfDownloadUrl: (contractId: string) =>
      `${API_BASE}/contracts/${encodeURIComponent(contractId)}/final-pdf/download-url`,
    finalize: (contractId: string) =>
      `${API_BASE}/contracts/${encodeURIComponent(contractId)}/finalize`,
    reviewStatus: (contractId: string) =>
      `${API_BASE}/contracts/${encodeURIComponent(contractId)}/review-status`,
  },

  // 기본/상세 수수료율 정책
  feeRates: {
    history: () => `${API_BASE}/fee-rates/history`,
    historyUser: () => `${API_BASE}/fee-rates/history/user`,
    policy: () => `${API_BASE}/fee-rates/policy`,
    policyCopy: () => `${API_BASE}/fee-rates/policy/copy`,
    detailGroups: () => `${API_BASE}/fee-rates/detail/groups`,
    detailGroupClinics: () => `${API_BASE}/fee-rates/detail/groups/clinics`,
    detailGroup: (id: number) => `${API_BASE}/fee-rates/detail/groups/${id}`,
    hospitalDetail: () => `${API_BASE}/fee-rates/detail/groups/hospital/detail`,
    clinicDetail: () => `${API_BASE}/fee-rates/detail/groups/clinic/detail`,
    hospitalDrugRules: () =>
      `${API_BASE}/fee-rates/detail/groups/hospital-drug-rules`,
    clinicPharmaRates: () =>
      `${API_BASE}/fee-rates/detail/groups/clinic-pharma-rates`,
    clinicDrugRates: () =>
      `${API_BASE}/fee-rates/detail/groups/clinic-drug-rates`,
  },

  // 세금계산서 역발행
  taxInvoices: {
    list: () => `${API_BASE}/v1/tax-invoices`,
    all: () => `${API_BASE}/v1/tax-invoices/all`,
    export: () => `${API_BASE}/v1/tax-invoices/export`,
    detail: (id: number) => `${API_BASE}/v1/tax-invoices/${id}`,
    upload: () => `${API_BASE}/v1/tax-invoices/upload`,
    issue: () => `${API_BASE}/v1/tax-invoices/issue`,
    cancel: () => `${API_BASE}/v1/tax-invoices/cancel`,
    resetPublished: () => `${API_BASE}/v1/tax-invoices/reset-published`,
    bulkTransfer: () => `${API_BASE}/v1/tax-invoices/bulk-transfer`,
  },

  // 의약품 데이터 워크플로
  drugData: {
    flags: () => `${API_BASE}/drug-data/flags`,
    setFlag: () => `${API_BASE}/drug-data/set-flag`,
    release: () => `${API_BASE}/drug-data/release`,
    reset: () => `${API_BASE}/drug-data/reset`,
    drugs: () => `${API_BASE}/drug-data/drugs`,
    drugCandidates: () => `${API_BASE}/drug-data/search/drug-candidates`,
    hiraNonBenefit: () => `${API_BASE}/drug-data/hira/non-benefit`,
    codeMapping: () => `${API_BASE}/drug-data/code-mapping`,
    commissionValidate: () =>
      `${API_BASE}/drug-data/commission/validate-drug-codes`,
    commissionUpload: () => `${API_BASE}/drug-data/commission`,
    hiraMerge: () => `${API_BASE}/drug-data/hira/merge`,
    updateDrugInfo: () => `${API_BASE}/drug-data/drug-info/update`,
    batchDataCollection: () => `${API_BASE}/batch/data-collection`,
    drugByPharmaCode: (refYm: string, drugCodePharma: string) =>
      `${API_BASE}/drug-data/drugs/${encodeURIComponent(refYm)}/pharma-code/${encodeURIComponent(drugCodePharma)}`,
  },

  // 게시판 (공지/FAQ)
  board: {
    notices: () => `${API_BASE}/notices`,
    notice: (id: number) => `${API_BASE}/notices/${id}`,
    noticeImages: () => `${API_BASE}/notices/images`,
    faqs: () => `${API_BASE}/faqs`,
    faq: (faqId: number) => `${API_BASE}/faqs/${faqId}`,
  },

  // 약관 (버전 관리)
  terms: {
    types: () => `${API_BASE}/terms`,
    detail: (termsId: number) => `${API_BASE}/terms/${termsId}`,
    versions: (type: string) =>
      `${API_BASE}/terms/types/${encodeURIComponent(type)}/versions`,
  },

  // 맥스매치
  maxMatch: {
    run: () => `${API_BASE}/max-match`,
    search: () => `${API_BASE}/max-match/search`,
    exists: () => `${API_BASE}/max-match/exists`,
    publish: () => `${API_BASE}/max-match/publish`,
    excelDetails: () => `${API_BASE}/max-match/excel-details`,
    userDetails: (userId: number) =>
      `${API_BASE}/max-match/users/${userId}/details`,
    userClinics: (userId: number) =>
      `${API_BASE}/max-match/users/${userId}/clinics`,
    operatingHours: () => `${API_BASE}/max-match/operating/hours`,
    screenWindows: () => `${API_BASE}/max-match/operating/screen-windows`,
    screenWindow: (screenType: string) =>
      `${API_BASE}/max-match/operating/screen-windows/${screenType}`,
    miniRun: () => `${API_BASE}/max-match/mini`,
    miniSearch: () => `${API_BASE}/max-match/mini/search`,
    miniExcel: (summaryId: number) =>
      `${API_BASE}/max-match/mini/${summaryId}/excel-details`,
    miniImages: (summaryId: number) =>
      `${API_BASE}/max-match/mini/${summaryId}/images`,
    miniMrs: (imageId: number) =>
      `${API_BASE}/max-match/mini/images/${imageId}/mrs`,
    miniMrsBatch: (imageId: number) =>
      `${API_BASE}/max-match/mini/images/${imageId}/mrs/batch`,
    miniMrsDeleteAll: (imageId: number) =>
      `${API_BASE}/max-match/mini/images/${imageId}/mrs`,
    miniMr: (mrId: number) => `${API_BASE}/max-match/mini/mrs/${mrId}`,
    miniConfirmImage: (imageId: number) =>
      `${API_BASE}/max-match/mini/images/${imageId}/confirm`,
    miniUnverifiableImage: (imageId: number) =>
      `${API_BASE}/max-match/mini/images/${imageId}/unverifiable`,
    miniConfirmBatch: () => `${API_BASE}/max-match/mini/images/confirm-batch`,
    miniPrescriptionInfo: (summaryId: number) =>
      `${API_BASE}/max-match/mini/${summaryId}/prescription-info`,
    /** 맥스매치 Full — `/admin/max-match/full` */
    fullSearch: () => `${API_BASE}/max-match/full/search`,
    fullDetail: (maxMatchFullPrescriptionId: number) =>
      `${API_BASE}/max-match/full/${maxMatchFullPrescriptionId}`,
    fullSource: (maxMatchFullPrescriptionId: number) =>
      `${API_BASE}/max-match/full/${maxMatchFullPrescriptionId}/source`,
    fullConfirmImage: (maxMatchFullPrescriptionId: number, imageId: number) =>
      `${API_BASE}/max-match/full/${maxMatchFullPrescriptionId}/images/${imageId}/confirm`,
    fullUnverifiableImage: (
      maxMatchFullPrescriptionId: number,
      imageId: number,
    ) =>
      `${API_BASE}/max-match/full/${maxMatchFullPrescriptionId}/images/${imageId}/unverifiable`,
    fullConfirmBatch: (maxMatchFullPrescriptionId: number) =>
      `${API_BASE}/max-match/full/${maxMatchFullPrescriptionId}/images/confirm-batch`,
    fullRuns: (maxMatchFullPrescriptionId: number) =>
      `${API_BASE}/max-match/full/${maxMatchFullPrescriptionId}/runs`,
    fullRun: (runId: number) => `${API_BASE}/max-match/full/runs/${runId}`,
    fullRunExcel: (runId: number) =>
      `${API_BASE}/max-match/full/runs/${runId}/excel-details`,
    // 맥스매치 운영 (Admin MaxMatch Ops)
    ops: () => `${API_BASE}/max-match/ops`,
    opsDetail: (summaryId: number) => `${API_BASE}/max-match/ops/${summaryId}`,
    opsImages: (summaryId: number) =>
      `${API_BASE}/max-match/ops/${summaryId}/images`,
    opsRun: (summaryId: number) => `${API_BASE}/max-match/ops/${summaryId}/run`,
    opsExcel: (summaryId: number) =>
      `${API_BASE}/max-match/ops/${summaryId}/excel-details`,
    opsPresignedUrls: () => `${API_BASE}/max-match/ops/images/presigned-urls`,
    opsMrs: (imageId: number) =>
      `${API_BASE}/max-match/ops/images/${imageId}/mrs`,
    opsMrsBatch: (imageId: number) =>
      `${API_BASE}/max-match/ops/images/${imageId}/mrs/batch`,
    opsMr: (mrId: number) => `${API_BASE}/max-match/ops/mrs/${mrId}`,
    opsConfirmImage: (imageId: number) =>
      `${API_BASE}/max-match/ops/images/${imageId}/confirm`,
    opsConfirmBatch: () => `${API_BASE}/max-match/ops/images/confirm-batch`,
    opsImage: (imageId: number) =>
      `${API_BASE}/max-match/ops/images/${imageId}`,
    miniWebOcrPostProcess: () =>
      `/api/v1/max-match/mini/web/prescriptions/ocr/post-process`,
  },
} as const;
